// src/hooks/useAsyncJob.ts
// ────────────────────────────────────────────────────────────────────────────
// Generic SSE polling hook for 202 Accepted workflows.
//
// When a BFF route returns 202 + { run_id, poll_url }, the FE opens an
// EventSource to the orchestrator stream proxy and waits for terminal events
// (step_completed, step_failed, workflow_done).
//
// Domain hooks compose this — they never duplicate SSE logic.
//   const job = useAsyncJob();
//   // After getting 202: job.start(run_id, poll_url);
//   // Read:              job.status, job.progress, job.result, job.error
// ────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Types ───────────────────────────────────────────────────────────────────

export type AsyncJobStatus =
  | 'idle'
  | 'connecting'
  | 'streaming'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'timeout';

export interface AsyncJobProgress {
  percent?: number;
  message?: string;
  handler_key?: string;
  [key: string]: unknown;
}

export interface AsyncJobResult {
  /** Raw outcome payload from the worker (handler-specific shape). */
  outcome: Record<string, unknown>;
  /** Run ID from the orchestrator. */
  run_id: string;
  /** Handler key that produced the result. */
  handler_key?: string;
  /** Step name that completed. */
  step_name?: string;
}

export interface UseAsyncJobReturn {
  /** Current job status. */
  status: AsyncJobStatus;
  /** Structured progress from SSE events. */
  progress: AsyncJobProgress | null;
  /** Final result on success. */
  result: AsyncJobResult | null;
  /** Error message on failure. */
  error: string | null;
  /** Active run ID (null when idle). */
  runId: string | null;
  /** Milliseconds elapsed since stream opened. */
  elapsedMs: number;
  /** Start polling a job. */
  start: (runId: string, pollUrl: string) => void;
  /** Cancel the current job and close the stream. */
  cancel: () => void;
  /** Reset to idle state. */
  reset: () => void;
}

// ── Constants ───────────────────────────────────────────────────────────────

/** Maximum client-side SSE duration (5 min). */
const MAX_STREAM_DURATION_MS = 5 * 60 * 1_000;

/** Elapsed timer tick (1 s). */
const ELAPSED_TICK_MS = 1_000;

/** SSE event names emitted by the orchestrator stream. */
const EVT_PROGRESS       = 'progress';
const EVT_STEP_COMPLETED = 'step_completed';
const EVT_STEP_FAILED    = 'step_failed';
const EVT_WORKFLOW_DONE  = 'workflow_done';
const EVT_ERROR          = 'error';

// ── Hook ────────────────────────────────────────────────────────────────────

export function useAsyncJob(): UseAsyncJobReturn {
  const [status, setStatus]     = useState<AsyncJobStatus>('idle');
  const [progress, setProgress] = useState<AsyncJobProgress | null>(null);
  const [result, setResult]     = useState<AsyncJobResult | null>(null);
  const [error, setError]       = useState<string | null>(null);
  const [runId, setRunId]       = useState<string | null>(null);
  const [elapsedMs, setElapsed] = useState(0);

  const esRef      = useRef<EventSource | null>(null);
  const timerRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startRef   = useRef<number>(0);

  // ── Cleanup ─────────────────────────────────────────────────────────
  const cleanup = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (timeoutRef.current) { clearTimeout(timeoutRef.current); timeoutRef.current = null; }
  }, []);

  // ── Cancel ──────────────────────────────────────────────────────────
  const cancel = useCallback(() => {
    cleanup();
    setStatus('cancelled');
  }, [cleanup]);

  // ── Reset ───────────────────────────────────────────────────────────
  const reset = useCallback(() => {
    cleanup();
    setStatus('idle');
    setProgress(null);
    setResult(null);
    setError(null);
    setRunId(null);
    setElapsed(0);
  }, [cleanup]);

  // ── Start ───────────────────────────────────────────────────────────
  const start = useCallback((id: string, pollUrl: string) => {
    cleanup();
    setStatus('connecting');
    setProgress(null);
    setResult(null);
    setError(null);
    setRunId(id);
    setElapsed(0);
    startRef.current = Date.now();

    // Elapsed timer
    timerRef.current = setInterval(() => {
      setElapsed(Date.now() - startRef.current);
    }, ELAPSED_TICK_MS);

    // Client-side timeout
    timeoutRef.current = setTimeout(() => {
      cleanup();
      setStatus('timeout');
      setError('Job timed out (client-side limit reached)');
    }, MAX_STREAM_DURATION_MS);

    // Open SSE
    const es = new EventSource(pollUrl);
    esRef.current = es;

    es.onopen = () => setStatus('streaming');

    es.addEventListener(EVT_PROGRESS, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        setProgress({
          percent:     data.percent ?? data.progress_pct,
          message:     data.message ?? data.status_message,
          handler_key: data.handler_key,
          ...data,
        });
      } catch { /* ignore malformed progress */ }
    });

    es.addEventListener(EVT_STEP_COMPLETED, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        cleanup();
        setStatus('completed');
        setResult({
          outcome:     data.outcome ?? data,
          run_id:      id,
          handler_key: data.handler_key,
          step_name:   data.step_name,
        });
      } catch { /* ignore */ }
    });

    es.addEventListener(EVT_WORKFLOW_DONE, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        // If we haven't already completed via step_completed:
        setStatus((prev) => prev === 'completed' ? prev : 'completed');
        setResult((prev) => prev ?? {
          outcome:     data.outcome ?? data,
          run_id:      id,
          handler_key: data.handler_key,
          step_name:   data.step_name,
        });
        cleanup();
      } catch { /* ignore */ }
    });

    es.addEventListener(EVT_STEP_FAILED, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        cleanup();
        setStatus('failed');
        setError(data.error ?? data.message ?? 'Step failed');
      } catch {
        cleanup();
        setStatus('failed');
        setError('Step failed (unknown error)');
      }
    });

    es.addEventListener(EVT_ERROR, (e) => {
      try {
        const data = JSON.parse((e as MessageEvent).data);
        cleanup();
        setStatus('failed');
        setError(data.error ?? data.message ?? 'Workflow error');
      } catch {
        cleanup();
        setStatus('failed');
        setError('Stream error');
      }
    });

    es.onerror = () => {
      // EventSource auto-reconnects on transient errors;
      // only mark failed if readyState is CLOSED (no reconnect).
      if (es.readyState === EventSource.CLOSED) {
        cleanup();
        setStatus('failed');
        setError('SSE connection closed unexpectedly');
      }
    };
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);

  return { status, progress, result, error, runId, elapsedMs, start, cancel, reset };
}
